import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Music, Crown, ExternalLink } from 'lucide-react';
import SEO from '../../components/SEO';

const ExploreMusic = () => {
  const subscriptionJoinLink = 'https://members.popcrownofficial.com/subscription/join';

  // Studio Albums in chronological order
  const studioAlbums = [
    {
      title: 'Got to Be There',
      year: '1972',
      cover: '/images/albums/gottobethere.jpg',
      description: 'Michael\'s debut solo album featuring the title track and "Rockin\' Robin".',
      color: '#8B4513',
      spotifyUrl: 'https://open.spotify.com/album/0F4XW0iBOhNFkbn1BuQ8cu'
    },
    {
      title: 'Ben',
      year: '1972',
      cover: '/images/albums/ben.jpg',
      description: 'Second solo album with the #1 hit title track from the film of the same name.',
      color: '#2F4F4F',
      spotifyUrl: 'https://open.spotify.com/album/3xo2soey9XEBI5DlmT4KlQ'
    },
    {
      title: 'Music & Me',
      year: '1973',
      cover: '/images/albums/musicandme.jpg',
      description: 'Third solo album showcasing Michael\'s maturing vocal style.',
      color: '#4682B4',
      spotifyUrl: 'https://open.spotify.com/album/19vhLDr0Fw8Lja1I8xVV09'
    },
    {
      title: 'Forever, Michael',
      year: '1975',
      cover: '/images/albums/forevermichael.jpg',
      description: 'Final Motown solo album before his move to Epic Records.',
      color: '#DAA520',
      spotifyUrl: 'https://open.spotify.com/album/6leeBdAohG7zhybC3Cmu8W'
    },
    {
      title: 'Off the Wall',
      year: '1979',
      cover: '/images/albums/offthewall.jpg',
      description: 'The breakthrough solo album with "Don\'t Stop \'Til You Get Enough" and "Rock with You".',
      color: '#FF6B35',
      spotifyUrl: 'https://open.spotify.com/album/2ZytN2cY4Zjrr9ukb2rqTP'
    },
    {
      title: 'Thriller',
      year: '1982',
      cover: '/images/albums/thriller.jpg',
      description: 'The best-selling album of all time with 70+ million copies sold worldwide.',
      color: '#DC143C',
      spotifyUrl: 'https://open.spotify.com/album/2ANVost0y2y52ema1E9xAZ'
    },
    {
      title: 'Bad',
      year: '1987',
      cover: '/images/albums/bad.png',
      description: 'Five #1 singles including "Bad", "The Way You Make Me Feel", and "Man in the Mirror".',
      color: '#000000',
      spotifyUrl: 'https://open.spotify.com/album/3Us57CjssWnHjTUIXBuIeH'
    },
    {
      title: 'Dangerous',
      year: '1991',
      cover: '/images/albums/dangerous.jpg',
      description: 'A fusion of new jack swing and industrial sounds with hits like "Black or White".',
      color: '#E10600',
      spotifyUrl: 'https://open.spotify.com/album/0oX4SealMgNXrvRDhqqOKg'
    },
    {
      title: 'HIStory',
      year: '1995',
      cover: '/images/albums/history.jpg',
      description: 'A double album featuring "Earth Song", "You Are Not Alone", and "They Don\'t Care About Us".',
      color: '#C0C0C0',
      spotifyUrl: 'https://open.spotify.com/album/3OBhnTLrvkoEEETjFA3Qfk'
    },
    {
      title: 'Blood on the Dance Floor',
      year: '1997',
      cover: '/images/albums/bloodonthedancefloor.jpg',
      description: 'Remix album with five new songs including the title track and "Ghosts".',
      color: '#8B0000',
      spotifyUrl: 'https://open.spotify.com/album/0RNsFWWdiz1rrdLI1pwbvJ'
    },
    {
      title: 'Invincible',
      year: '2001',
      cover: '/images/albums/invincible.jpg',
      description: 'The final studio album during Michael\'s lifetime featuring "You Rock My World".',
      color: '#4169E1',
      spotifyUrl: 'https://open.spotify.com/album/52E4RP7XDzalpIrOgSTgiQ'
    }
  ];

  // Compilations & Posthumous Releases
  const compilationsAndPosthumous = [
    {
      title: 'Number Ones',
      year: '2003',
      cover: '/images/albums/numberones.jpg',
      description: 'Greatest hits collection featuring 18 #1 singles.',
      color: '#E10600',
      spotifyUrl: 'https://open.spotify.com/album/1jcYwZsN7JEve9xsq9BuUX'
    },
    {
      title: 'Thriller 25',
      year: '2008',
      cover: '/images/albums/thriller25.jpg',
      description: '25th anniversary edition with new remixes and bonus tracks.',
      color: '#DC143C',
      spotifyUrl: 'https://open.spotify.com/album/1C2h7mLntPSeVYciMRTF4a'
    },
    {
      title: 'This Is It',
      year: '2009',
      cover: '/images/albums/thisisit.jpg',
      description: 'Soundtrack from the documentary featuring rehearsal recordings.',
      color: '#E10600',
      spotifyUrl: 'https://open.spotify.com/album/7pMVCMwGykuEu9rzTHxLCm',
      posthumous: true
    },
    {
      title: 'Michael',
      year: '2010',
      cover: '/images/albums/michael2010.jpg',
      description: 'First posthumous album with previously unreleased material.',
      color: '#1E90FF',
      spotifyUrl: 'https://open.spotify.com/album/4R9ftTftsKlE4Y5gJ7Jj73',
      posthumous: true
    },
    {
      title: 'Immortal',
      year: '2011',
      cover: '/images/albums/immortal.jpg',
      description: 'Companion album to Cirque du Soleil\'s "Michael Jackson: The Immortal World Tour".',
      color: '#9400D3',
      spotifyUrl: 'https://open.spotify.com/album/5ReKddpdZKgpVlecQhLnEH',
      posthumous: true
    },
    {
      title: 'Bad 25',
      year: '2012',
      cover: '/images/albums/bad25.jpg',
      description: '25th anniversary edition with bonus disc of demos and unreleased tracks.',
      color: '#000000',
      spotifyUrl: 'https://open.spotify.com/album/24TAupSNVWSAHL0R7n71vm',
      posthumous: true
    },
    {
      title: 'Xscape',
      year: '2014',
      cover: '/images/albums/xscape.jpg',
      description: 'Second posthumous album featuring contemporised unreleased recordings.',
      color: '#FF1493',
      spotifyUrl: 'https://open.spotify.com/album/7pomP86PUhoJpY3fsC0WDQ',
      posthumous: true
    },
    {
      title: 'Thriller 40',
      year: '2022',
      cover: '/images/albums/thriller40.jpg',
      description: '40th anniversary edition with bonus disc of demos and rare tracks.',
      color: '#DC143C',
      spotifyUrl: 'https://open.spotify.com/album/57TzZhbqvYoUBzJSVKFVlG',
      posthumous: true
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      <SEO title="Michael Jackson Music — Complete Discography & Albums | PopCrown" description="Explore Michael Jackson's complete discography. Every album from Off the Wall through Thriller, Bad, Dangerous, HIStory, and Invincible." keywords="Michael Jackson discography, MJ albums, Thriller, Bad, Dangerous, HIStory, Off the Wall, Invincible, Michael Jackson music, King of Pop discography" canonical="/explore/music" />
      {/* Header */}
      <section className="py-12" style={{ background: 'linear-gradient(180deg, #1a1a1a 0%, #000 100%)' }}>
        <div className="container mx-auto px-4">
          <Link to="/explore" className="inline-flex items-center gap-2 text-[#E10600] hover:text-white transition-colors mb-6">
            <ArrowLeft className="w-5 h-5" />
            Back to Explore
          </Link>
          <div className="flex items-center gap-4">
            <Music className="w-12 h-12 text-[#1DB954]" />
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white">
                <span className="thriller-text">Music</span>
              </h1>
              <p className="text-gray-400">Explore the complete music collection</p>
            </div>
          </div>
        </div>
      </section>

      {/* Albums Grid */}
      <section className="py-12" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #FFF5F5 100%)' }}>
        <div className="container mx-auto px-4">
          <div className="mb-8 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-black mb-2">
              <span className="thriller-text">Albums</span>
            </h2>
            <p className="text-gray-600">The complete album collection (1972-2001)</p>
            <div className="mx-auto mt-3 h-1 w-16 bg-gradient-to-r from-[#DC143C] via-[#E10600] to-[#DC143C]"></div>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6 max-w-6xl mx-auto">
            {studioAlbums.map((album, index) => (
              <a
                key={index}
                href={album.spotifyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group text-center transition-all duration-300 hover:transform hover:scale-105"
              >
                {/* Album Cover */}
                <div className="relative rounded-lg overflow-hidden shadow-xl mb-3" style={{
                  boxShadow: '0 8px 30px rgba(0,0,0,0.3)'
                }}>
                  <img
                    src={album.cover}
                    alt={`${album.title} - Michael Jackson album cover ${album.year} - King of Pop discography - MJ music`}
                    className="w-full aspect-square object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  {/* Fallback */}
                  <div className="hidden w-full aspect-square items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                    <Music className="w-12 h-12 text-gray-600" />
                  </div>
                  {/* Hover overlay with Spotify play button */}
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-14 h-14 rounded-full bg-[#1DB954] flex items-center justify-center">
                      <svg className="w-6 h-6 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                  </div>
                </div>
                
                {/* Album Info */}
                <h3 className="text-sm font-bold text-gray-900 group-hover:text-[#1DB954] transition-colors leading-tight">{album.title}</h3>
                <p className="text-xs text-gray-500">{album.year}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Compilations & Posthumous Releases */}
      <section className="py-12" style={{ background: 'linear-gradient(180deg, #FFF5F5 0%, #FFFFFF 100%)' }}>
        <div className="container mx-auto px-4">
          <div className="mb-8 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-black mb-2">
              Compilations & <span className="thriller-text">Legacy Releases</span>
            </h2>
            <p className="text-gray-600">Greatest hits, anniversaries, and posthumous albums (2003-2022)</p>
            <div className="mx-auto mt-3 h-1 w-16 bg-gradient-to-r from-[#DC143C] via-[#E10600] to-[#DC143C]"></div>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-4 max-w-6xl mx-auto">
            {compilationsAndPosthumous.map((album, index) => (
              <a
                key={index}
                href={album.spotifyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group text-center transition-all duration-300 hover:transform hover:scale-105"
              >
                {/* Album Cover */}
                <div className="relative rounded-lg overflow-hidden shadow-xl mb-3" style={{
                  boxShadow: '0 8px 30px rgba(0,0,0,0.3)'
                }}>
                  <img
                    src={album.cover}
                    alt={`${album.title} - Michael Jackson album cover ${album.year} - King of Pop discography - MJ music`}
                    className="w-full aspect-square object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  {/* Fallback */}
                  <div className="hidden w-full aspect-square items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                    <Music className="w-12 h-12 text-gray-600" />
                  </div>
                  {/* Posthumous badge */}
                  {album.posthumous && (
                    <div className="absolute top-1 right-1 px-1.5 py-0.5 bg-black/70 rounded text-[10px] text-white">
                      Posthumous
                    </div>
                  )}
                  {/* Hover overlay with Spotify play button */}
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-10 h-10 rounded-full bg-[#1DB954] flex items-center justify-center">
                      <svg className="w-4 h-4 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                  </div>
                </div>
                
                {/* Album Info */}
                <h3 className="text-xs font-bold text-gray-900 group-hover:text-[#1DB954] transition-colors leading-tight">{album.title}</h3>
                <p className="text-[10px] text-gray-500">{album.year}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16" style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #000 100%)' }}>
        <div className="container mx-auto px-4 text-center">
          <img src="/popcrown-crown-red.png" alt="" aria-hidden="true" className="w-10 h-10 mx-auto mb-4 object-contain" />
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to <span className="thriller-text">Join Us</span>?
          </h2>
          <p className="text-gray-400 mb-6 max-w-xl mx-auto">
            Get access to interactive quizzes, community discussions, and exclusive features.
          </p>
          <a href={subscriptionJoinLink} target="_blank" rel="noopener noreferrer" className="btn-gold inline-block">
            Become a Member
          </a>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-6 bg-black">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-500 text-xs">
            Music streamed via Spotify. PopCrown is not affiliated with any artist estates or rights holders.
          </p>
        </div>
      </section>
    </div>
  );
};

export default ExploreMusic;
